import { isFormFunction, type TFormFactor } from 'components/form/types/attributes'
import type { FormFieldRef, FormNode, TFormFieldDict } from 'components/form/types/field'
import type { StringIndexable } from '@/types/object'
import { isFactor, isFunction, isNone, isString } from '@/utils/is'
import { toString } from '@/utils/formatting'
import { isRef, type Ref } from 'vue'
import { parseDate } from '@/utils/formatting/date'
import { isDate } from '@/utils/is'
import type { ValueOf } from '@/utils'
import type { IForm } from 'components/form/types/form'
import type { TFormModel } from 'components/form/types/formModel'
import type { FormV2UserState } from 'components/form/types/state'
import { toBoolean } from '@/utils/boolean'
import { isFindexFile } from '@/types/file'
import type { FormSchemaReducer } from 'components/form/types'
import type { FormHandler } from '.'
import { toNumber } from '@/utils/number'
import { isNumber, isNumerical } from 'math'
import { isValue, type IValue } from 'findef'
import type { IInputProps } from 'components/form/input/types'
import { uref } from '@/utils/reactive/uref'

export const isFormNode = (v: any): v is FormNode => {
  if (!v) return false;
  if (typeof v !== 'object') return false;
  return !!('$ref' in v);
}

export const isFieldRef = (v: any): v is FormFieldRef => {
  if (!v) return false;
  if (!isRef(v)) return false;
  const val = v.value;
  if (!val) return false;
  if (typeof val !== 'object') return false;
  return !!('schema' in val);
}

export const isFormFieldDict = <T extends TFormModel = {[key: string]: any}>(v: any): v is TFormFieldDict<T> => {
  if (!v) return false;
  if (typeof v !== 'object') return false;
  return !!('$ref' in v);
}

export const isFormHandler = (v: any): v is FormHandler => {
  if (!v) return false;
  if (typeof v !== 'object') return false;
  if (!('isFormHandler' in v)) return false;
  return v.isFormHandler === 'yes';
}

export const runSchemaReducers = <T extends StringIndexable = StringIndexable>(
  schema: IForm<T>,
  state: T,
  reducers: FormSchemaReducer<T>[]
): IForm<T> => {
  return reducers.reduce((prev, cur) => {
    return { ...prev, ...cur(prev, state) }
  }, schema)
}

export const getValidatedFieldsCount = <T extends StringIndexable = StringIndexable, ContextType extends (StringIndexable | undefined) = StringIndexable>(
  schema: IForm<T, ContextType>
): number => {
  let count: number = 0

  const hasValidators = (obj: any): obj is StringIndexable => {
    const keys = Object.keys(obj)
    return keys.includes('$validators') && !!obj['$validators']
  }

  const values = Object.values(schema) as Array<any>

  for (const value of values) {
    if (typeof value !== 'object') continue
    if (isDate(value)) continue
    if (isFunction(value)) continue
    if (isNone(value)) continue

    if (hasValidators(value)) {
      const validators = value['$validators'] as any[]
      if (validators.length > 0) count += 1
    } else {
      count += getValidatedFieldsCount(value as IForm<any>)
    }
  }
  return count
}

export const evaluateFormState = <T extends StringIndexable = StringIndexable, ContextType extends (StringIndexable | undefined) = StringIndexable>(
  schema: IForm<T, ContextType>,
  fields: TFormFieldDict<T>
): T => {
  return castFormState<T, ContextType>(
    schema,
    Object.assign(
      {},
      ...Object.entries(fields)
        .filter(([k, v]) => !k.startsWith('$') && !isFunction(v))
        .map(([k, v]) => {

          if (isRef(v) && !isNone(v.value)) {
            return { [k]: castFieldValue((schema || {}) as any, v.value.value)  };
          }

          return {
            [k]: evaluateFormState((schema as any)[k] || {} as typeof schema, v as any)
          }
        })
    )
  )
}

export const isRenderableField = <T extends StringIndexable = StringIndexable, ContextType extends (StringIndexable | undefined) = StringIndexable>(
  schema?: IForm<T, ContextType>
): boolean => {
  if (!schema) return true
  const keys = Object.keys(schema)
  return keys.includes('$value') || keys.includes('$type') || keys.includes('$customComponent')
}

export const fieldTypeToJavascript = (typename: string, _componentType?: string): string => {
if (typeof typename === 'string' && typename.startsWith('vec')) {
    const parts = typename.split('vec');
    if (parts.length > 0) {
      const right = parts[1];
      if (isNumerical(right)) return typename;
    }
  }

  switch (typename) {
    case 'number[]':
      return typename;
    case 'value':
      return typename;
    case 'text':
    case 'email':
    case 'password':
    case 'textarea':
    case 'string':
      return 'string'
    case 'float':
    case 'int':
    case 'integer':
    case 'currency':
    case 'number':
      return 'number'
    case 'bool':
    case 'boolean':
    case 'switch':
    case 'radio':
      return 'boolean'
    case 'date':
      return 'date'
    case 'display':
      return 'display'
    case 'header':
      return 'header'
    case 'image':
      return 'image';
    case 'file':
      return 'file';
    case 'attachment':
      return 'attachment';
    case 'vector':
    case 'vec2':
    case 'vec3':
    case 'vec4':
      return 'vector';
    case 'object':
    case 'DBObject':
    case 'dbobject':
    case 'dbObject':
      return 'object';
  }

  throw new Error(`Invalid field type ${typename}`)
}

export const castByName = (
  value: any,
  typename: string,
  componentType?: string
): string | number | boolean | Date | TFormFactor => {
  const typ = fieldTypeToJavascript(typename, componentType)

  if (typ.startsWith('vec')) {
    const parts = typ.split('vec');
    if (parts.length > 0) {
      const right = parts[1];
      if (isNumerical(right)) return value;
    }
  }
  switch (typ) {
    case 'object': {
      if (typeof value === 'object') return value;
      if (typeof value === 'string' && value.length > 1) {
        try {
          return JSON.parse(value);
        } catch (e) {
          console.error(`COULD NOT PARSE ${value}`)
          console.error(e)
        }
      }
      return null;
    }
    case 'display':
      return toString(value)
    case 'header':
      return toString(value)
    case 'string': {
      if (typeof value === 'string') return value

      if (value) {
        if (Array.isArray(value) && value.length > 0) {
          const first = value[0]
          if (first && (isFindexFile(first) || isString(first.base64))) {
            return castByName(first, typename, componentType)
          }
        }

        if (isFindexFile(value) || isString(value.base64)) {
          return value.base64 || ''
        }
      }

      return toString(value)
    }
    case 'number': {
      if (isNone(value)) return 0;
      return Number(value);
    };
    case 'value': {
      if (isValue(value)) return value;
      if (isNumber(value)) {
        const val: IValue = {
          value: value,
          time: new Date(),
          percentage: 0
        };
        return val;
      }
      if (isString(value)) {
        const val: IValue = {
          value: Number(value),
          time: new Date(),
          percentage: 0
        };
        return val;
      }
      const val: IValue = {
        value: 0,
        time: new Date(),
        percentage: 0
      };
      return val;
    };
    case 'boolean':
      return toBoolean(value)
    case 'bool':
      return toBoolean(value)
    case 'date':
      return parseDate(value)
    case 'image':
      return value;
    case 'file':
      return value;
    case 'vector':
    case 'vec2':
    case 'vec3':
    case 'vec4':
      return value;
    case 'attachment':
      return value;
    case 'number[]':
      return Array.isArray(value) ? value.map(it => toNumber(it)) : value;
  }

  throw new Error(`Could not cast to ${typename}`)
}

export const castFieldValue = (schema: IForm<any>, value: any) => {
  if (!schema) return value ?? null;
  return (schema.$type ? castByName(value, schema.$type || 'text', schema.$componentType) : value) ?? null;
}

const unref = (v: any): any => {
  if (isRef(v)) v = v.value
  if (isRef(v)) return unref(v)
  return v
}

export const unwrapFormValue = (v?: any): any => {
  if (typeof v === 'undefined') return ''
  if (isRef(v)) return unwrapFormValue(unref(v))
  return v
}

export const purifyForm = <T extends StringIndexable = {}>(
  item: IForm<T> | StringIndexable,
  state?: T
): T => {
  const next: T = {} as T

  if (!item) return item

  if (isRef(item)) {
    return unwrapFormValue(item.value as T[keyof T])
  }

  if ('$value' in item && isFunction(item['$value']) === false) {
    return unwrapFormValue(item['$value'])
  }

  for (const [k, v] of Object.entries(item)) {
    if (k.startsWith('$') || isFactor(v)) continue
    next[k as keyof T] = purifyForm(v, state) as T[keyof T]
  }

  return unwrapFormValue(next)
}

//export const castFormState = <T extends TFormModel = {}>(schema: IForm<T>, state: T): T => {
//
//  const cast = (key: string, value: any) => {
//    const sch: IForm<T> = schema[key as keyof typeof schema] as unknown as any as IForm<T>;
//    const typename = sch.$type || 'text';
//    const componentType = sch.$componentType;
//    const casted = castByName(value, typename, componentType);
//    return casted;
//  }
//
//  return Object.assign(
//    {},
//    ...Object.entries(state).filter(([k]) => !k.startsWith('$')).map(([key, field]: [keyof typeof state, ValueOf<typeof state>]) => {
//      if (Array.isArray(field)) return {[key]: field}; // TODO: maybe allow this?
//      if (isFactor(field)) return { [key]: cast(key as string, field) }
//      if (typeof field !== 'object' || isDate(field)) return {[key]: field};
//      return { [key]: castFormState((schema as any)[key as keyof typeof schema] as IForm<T>, field as unknown as any as T) }
//    })
//  );
//}

export const castFormState = <T extends TFormModel = {}, ContextType extends (StringIndexable | undefined) = StringIndexable>(schema: IForm<T, ContextType>, state: T): T => {
  const cast = (key: string, value: any) => {
    const sch: IForm<T> = schema[key as keyof typeof schema] as unknown as any as IForm<T>;
    if (!sch) return value;
    const typename = sch.$type || 'text'
    const componentType = sch.$componentType
    const casted = castByName(value, typename, componentType)
    return casted
  }
  return Object.assign(
    {},
    ...Object.entries(state)
      .filter(([k]) => !k.startsWith('$'))
      .map(([key, field]: [keyof typeof state, ValueOf<typeof state>]) => {
        if (Array.isArray(field)) return { [key]: field } // TODO: maybe allow this?
        if (isFactor(field)) return { [key]: cast(key as string, field) }
        if (typeof field !== 'object' || field === null || isDate(field)) return { [key]: field }

        const nestedSchema = (schema as any)[key as keyof typeof schema] as IForm<T>;
        if (nestedSchema && field) {
          return {
            [key]: castFormState(
              nestedSchema,
              field as unknown as any as T
            )
          }
        } else {
          return { [key]: field }
        }
      })
  )
}

export const applyForm = <T extends TFormModel = {}, ContextType extends (StringIndexable | undefined) = StringIndexable>(
  form: IForm<T, ContextType> | FormV2UserState<T>,
  data: FormV2UserState<T>,
  _top?: FormV2UserState<T>
): IForm<T, ContextType> => {
  const next: IForm<T, ContextType> = { ...form } as IForm<T, ContextType>

  if (typeof data !== 'object') return next

  const entries: [keyof IForm<T>, any][] = Object.entries(form) as [keyof IForm<T>, any][]
  for (const [k, v] of entries) {
    if (!(k in data)) continue

    if (isFunction(v)) continue
    if (typeof v === 'object' && !isDate(v)) {
      if ('$value' in v) {
        (next as any)[k].$value = unwrapFormValue((data as any)[k])
      } else if (typeof (next as any)[k] === 'object' && !isDate((next as any)[k])) {
        ;(next as any)[k] = applyForm<T, ContextType>(
          (next as any)[k] as T[keyof T],
          unwrapFormValue((data as any)[k]),
          data
        ) as T[keyof T]
      }
    }
  }
  return next
}

export const evaluateReactiveProps = <T extends TFormModel = { [key: string]: any }, ContextType extends (StringIndexable | undefined) = StringIndexable>(
  schema: IForm<T, ContextType>,
  field: FormFieldRef,
  outerSchema: IForm<T, ContextType>,
  fields: TFormFieldDict<T>,
  context?: Ref<ContextType>,
  sourceField?: FormFieldRef
) => {

  const skip = !!(sourceField && sourceField.value && sourceField.value.path && field.value && field.value.path === sourceField.value.path);

  const getContext = () => ({
    state: evaluateFormState(outerSchema, fields),
    fields: fields,
    context: context,
    path: getFormFieldPath(field) || schema.$name || ''
  })

  if (!skip && isFunction(schema.$value)) {
    field.value.value = schema.$value(getContext())
  }
  if (isFunction(schema.$hidden)) {
    field.value.hidden = toBoolean(schema.$hidden(getContext()));
  }
  if (isFunction(schema.$locked)) {
    field.value.locked = toBoolean(schema.$locked(getContext()));
  }
  if (isFunction(schema.$lockedReason)) {
    field.value.lockedReason = toString(
      schema.$lockedReason(getContext())
    )
  }
  if (isFunction(schema.$placeholder)) {
    field.value.placeholder = toString(
      schema.$placeholder(getContext())
    )
  }
  if (isFunction(schema.$display)) {
    field.value.display = schema.$display(getContext())
  }
  if (isFormFunction<T, ContextType>(schema.$injectedProps, '$injectedProps')) {
    field.value.injectedProps = schema.$injectedProps(getContext())
  }
}

export const patchAllFields = <T extends TFormModel = { [key: string]: any }>(
  fields: TFormFieldDict<T>,
  fun: (field: FormFieldRef, key: string) => void
) => {

  for (const [key, item] of Object.entries(fields)) {
    if (isFormHandler(item)) {
      continue;
    }
    if (isFieldRef(item)) {
      fun(item, item.value.name || key);
      continue;
    }
    if (isFormFieldDict(item)) {
      patchAllFields(item as TFormFieldDict<T>, fun);
      continue;
    }
  }
}

export const getFormFieldPath = (field?: FormFieldRef | IInputProps): string | undefined => {
  if (!field) return undefined;
  const f = uref(field);
  if (!f) return undefined;
  if (f.path && typeof f.path === 'string') return f.path;
  if (f.name && typeof f.name === 'string') return f.name;
  return undefined;
}
